import { ROSTER_SORT_OPTIONS } from '../constants/filters';
import { RosterMember } from '../graphql-types/roster';

type WTNGroupedPlayers = {
  proPlayers: RosterMember[];
  otherPlayers: RosterMember[];
  withoutWtn: RosterMember[];
};

export type RosterListOptionsType = 'alphabetically' | 'wtnAsc' | 'wtnDesc';

const IS_ASCENDING_ORDER = true;

export const nameSort = (a: RosterMember, b: RosterMember) => {
  const nameA = `${a?.standardGivenName}${a?.standardFamilyName}`.toLowerCase();
  const nameB = `${b?.standardGivenName}${b?.standardFamilyName}`.toLowerCase();
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }

  return 0;
};

const wtnSort = (a: RosterMember, b: RosterMember, ascending: boolean) => {
  const direction = ascending ? -1 : 1;
  const wtnSingleA = a.worldTennisNumbers?.find(wtn => wtn.type === 'SINGLE');
  const wtnSingleB = b.worldTennisNumbers?.find(wtn => wtn.type === 'SINGLE');

  const wtnNumberA = wtnSingleA?.tennisNumber ?? 0;
  const wtnNumberB = wtnSingleB?.tennisNumber ?? 0;
  if (wtnNumberA > wtnNumberB) {
    return direction;
  }
  if (wtnNumberA < wtnNumberB) {
    return -direction;
  }

  return 0;
};

const groupRosterMembersByWTN = (members: RosterMember[]): WTNGroupedPlayers => {
  return members.reduce<WTNGroupedPlayers>(
    (acc, member) => {
      const wtnSingle = member.worldTennisNumbers?.find(wtn => wtn.type === 'SINGLE');
      const ranked = wtnSingle?.isRanked;
      if (ranked) {
        return { ...acc, proPlayers: [...acc.proPlayers, member], otherPlayers: acc.otherPlayers };
      }
      if (typeof wtnSingle?.tennisNumber === 'number') {
        return { ...acc, proPlayers: acc.proPlayers, otherPlayers: [...acc.otherPlayers, member] };
      }
      return { ...acc, proPlayers: acc.proPlayers, withoutWtn: [...acc.withoutWtn, member] };
    },
    { proPlayers: [], otherPlayers: [], withoutWtn: [] }
  );
};

export const getSortedRosterMembers = (
  members: RosterMember[],
  sortOrder: RosterListOptionsType | null,
  customSort?: (a: RosterMember, b: RosterMember) => number
): RosterMember[] => {
  if (customSort) {
    return [...members].sort(customSort);
  }

  switch (sortOrder) {
    case ROSTER_SORT_OPTIONS[0].value: {
      return [...members].sort(nameSort);
    }
    case ROSTER_SORT_OPTIONS[1].value: {
      const { proPlayers, otherPlayers, withoutWtn } = groupRosterMembersByWTN(members);
      return [
        ...otherPlayers.sort((a, b) => wtnSort(a, b, IS_ASCENDING_ORDER) ?? nameSort(a, b)),
        ...proPlayers,
        ...withoutWtn
      ];
    }
    case ROSTER_SORT_OPTIONS[2].value: {
      const { proPlayers, otherPlayers, withoutWtn } = groupRosterMembersByWTN(members);
      return [
        ...proPlayers,
        ...otherPlayers.sort((a, b) => wtnSort(a, b, !IS_ASCENDING_ORDER) ?? nameSort(a, b)),
        ...withoutWtn
      ];
    }
    default:
      return [...members].sort(nameSort);
  }
};
